/**
 * BulkDisputeWizard Component
 * 
 * Multi-step wizard for creating bulk dispute packages.
 * Features a progress bar and handles warnings, bureau selection, and strategy confirmation.
 */

import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  X,
  ChevronLeft,
  ChevronRight,
  CheckCircle,
  AlertTriangle,
  Shield,
  Target,
  FileText,
  CreditCard,
  Search,
  FileSearch,
  User,
  Building,
  Info,
  ShieldAlert
} from 'lucide-react';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Badge } from '../ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { cn } from '../../lib/utils';
import { useDisputeTray, DISPUTE_CATEGORIES, CATEGORY_LABELS } from '../../context/DisputeTrayContext';
import { getDisputeEligibility } from '../../utils/disputeEligibility';
import { analyzeInquiry } from '../../utils/inquiryAnalysis';

// Wizard steps
const STEPS = [
  { id: 'review', label: 'Review Items', icon: FileText },
  { id: 'warnings', label: 'Check Warnings', icon: AlertTriangle },
  { id: 'bureaus', label: 'Bureau Targets', icon: Target },
  { id: 'confirm', label: 'Confirm & Create', icon: CheckCircle }
];

// Category icons
const CATEGORY_ICONS = {
  [DISPUTE_CATEGORIES.ACCOUNTS]: CreditCard,
  [DISPUTE_CATEGORIES.INQUIRIES]: Search,
  [DISPUTE_CATEGORIES.PUBLIC_RECORDS]: FileSearch,
  [DISPUTE_CATEGORIES.PERSONAL_INFO]: User,
  [DISPUTE_CATEGORIES.CREDITOR_CONTACTS]: Building
};

/**
 * Progress Bar Component
 */
const ProgressBar = ({ currentStep, steps }) => {
  const currentIndex = steps.findIndex(s => s.id === currentStep);
  
  return (
    <div className="flex items-center justify-between mb-8">
      {steps.map((step, index) => {
        const Icon = step.icon;
        const isActive = index === currentIndex;
        const isComplete = index < currentIndex;
        
        return (
          <React.Fragment key={step.id}>
            <div className="flex flex-col items-center">
              <div 
                className={cn(
                  'w-10 h-10 rounded-full flex items-center justify-center transition-colors',
                  isComplete ? 'bg-emerald-500 text-white' :
                  isActive ? 'bg-emerald-100 text-emerald-600 ring-2 ring-emerald-500' :
                  'bg-slate-100 text-slate-400'
                )}
              >
                {isComplete ? <CheckCircle size={20} /> : <Icon size={20} />}
              </div>
              <span className={cn(
                'text-xs mt-2 font-medium',
                isActive ? 'text-emerald-600' : 'text-slate-500'
              )}>
                {step.label}
              </span>
            </div>
            
            {index < steps.length - 1 && (
              <div className={cn(
                'flex-1 h-1 mx-2 rounded',
                index < currentIndex ? 'bg-emerald-500' : 'bg-slate-200'
              )} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

/**
 * Review Step Component
 */
const ReviewStep = ({ selectedItems, onRemoveItem, categoryCounts }) => {
  const activeCategories = Object.entries(categoryCounts)
    .filter(([_, count]) => count > 0)
    .map(([category]) => category);
  
  const getItemName = (item, category) => {
    switch (category) {
      case DISPUTE_CATEGORIES.ACCOUNTS:
        return item.account_name || item.creditor_name || 'Unknown Account';
      case DISPUTE_CATEGORIES.INQUIRIES:
        return item.company_name || item.creditor || 'Unknown Inquiry';
      case DISPUTE_CATEGORIES.PUBLIC_RECORDS:
        return (item.type || item.record_type || 'Public Record').replace(/_/g, ' ');
      case DISPUTE_CATEGORIES.PERSONAL_INFO:
        return item.field || item.label || 'Personal Info';
      case DISPUTE_CATEGORIES.CREDITOR_CONTACTS:
        return item.name || item.raw_name || 'Creditor Contact';
      default:
        return 'Unknown';
    }
  };
  
  const getItemId = (item) => {
    return item.tradeline_id || item.inquiry_id || item.record_id || item.id || item.field;
  };
  
  return (
    <div className="space-y-4">
      <div className="p-4 bg-slate-50 rounded-xl border border-slate-200">
        <h4 className="font-medium text-stone-800 mb-2">Review your selections</h4>
        <p className="text-sm text-slate-600">
          You have selected {Object.values(categoryCounts).reduce((a, b) => a + b, 0)} items 
          across {activeCategories.length} categories. Review and remove any items you don't 
          want to include in this dispute package.
        </p>
      </div>
      
      <div className="space-y-3 max-h-[400px] overflow-y-auto">
        {activeCategories.map(category => {
          const Icon = CATEGORY_ICONS[category];
          const items = selectedItems[category] || [];
          
          return (
            <div key={category} className="border border-slate-200 rounded-xl overflow-hidden">
              <div className="flex items-center gap-2 p-3 bg-slate-50 border-b border-slate-200">
                <Icon size={18} className="text-slate-600" />
                <span className="font-medium text-stone-800">{CATEGORY_LABELS[category]}</span>
                <Badge variant="secondary">{items.length}</Badge>
              </div>
              <div className="divide-y divide-slate-100">
                {items.map(item => (
                  <div 
                    key={getItemId(item)}
                    className="flex items-center justify-between p-3 hover:bg-slate-50"
                  >
                    <span className="text-sm text-slate-700">{getItemName(item, category)}</span>
                    <button
                      onClick={() => onRemoveItem(category, getItemId(item))}
                      className="text-slate-400 hover:text-red-500 p-1"
                    >
                      <X size={16} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

/**
 * Warnings Step Component
 */
const WarningsStep = ({ selectedItems, tradelines = [] }) => {
  // Analyze items for warnings
  const warnings = useMemo(() => {
    const result = {
      accounts: { safe: [], caution: [] },
      inquiries: { safe: [], caution: [] },
      hasWarnings: false
    };
    
    // Check accounts for caution
    (selectedItems[DISPUTE_CATEGORIES.ACCOUNTS] || []).forEach(account => {
      const eligibility = getDisputeEligibility(account);
      if (eligibility.status === 'CAUTION') {
        result.accounts.caution.push({
          ...account,
          reason: eligibility.reason
        });
        result.hasWarnings = true;
      } else {
        result.accounts.safe.push(account);
      }
    });
    
    // Check inquiries for caution
    (selectedItems[DISPUTE_CATEGORIES.INQUIRIES] || []).forEach(inquiry => {
      const analysis = analyzeInquiry(inquiry, tradelines, inquiryItems);
      if (analysis.isCaution) {
        result.inquiries.caution.push({
          ...inquiry,
          reason: analysis.reason
        });
        result.hasWarnings = true;
      } else {
        result.inquiries.safe.push(inquiry);
      }
    });
    
    return result;
  }, [selectedItems, tradelines]);
  
  const totalCaution = warnings.accounts.caution.length + warnings.inquiries.caution.length;
  const totalSafe = warnings.accounts.safe.length + warnings.inquiries.safe.length + 
    (selectedItems[DISPUTE_CATEGORIES.PUBLIC_RECORDS]?.length || 0) +
    (selectedItems[DISPUTE_CATEGORIES.PERSONAL_INFO]?.length || 0);
  
  return (
    <div className="space-y-4">
      {/* Summary */}
      <div className="grid grid-cols-2 gap-4">
        <div className="p-4 bg-emerald-50 rounded-xl border border-emerald-200">
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle size={20} className="text-emerald-600" />
            <span className="font-medium text-emerald-800">Safe Items</span>
          </div>
          <p className="text-2xl font-bold text-emerald-700">{totalSafe}</p>
          <p className="text-xs text-emerald-600">Ready to dispute</p>
        </div>
        
        <div className={cn(
          'p-4 rounded-xl border',
          totalCaution > 0 ? 'bg-amber-50 border-amber-200' : 'bg-slate-50 border-slate-200'
        )}>
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle size={20} className={totalCaution > 0 ? 'text-amber-600' : 'text-slate-400'} />
            <span className={cn('font-medium', totalCaution > 0 ? 'text-amber-800' : 'text-slate-600')}>
              Review Before Disputing
            </span>
          </div>
          <p className={cn('text-2xl font-bold', totalCaution > 0 ? 'text-amber-700' : 'text-slate-500')}>
            {totalCaution}
          </p>
          <p className="text-xs text-slate-500">May need verification</p>
        </div>
      </div>
      
      {/* No Warnings */}
      {!warnings.hasWarnings && (
        <div className="p-6 bg-emerald-50 rounded-xl border border-emerald-200 text-center">
          <CheckCircle size={40} className="mx-auto text-emerald-500 mb-3" />
          <h4 className="font-medium text-emerald-800 mb-1">All items look good!</h4>
          <p className="text-sm text-emerald-600">
            No warnings detected for your selected items. You can proceed with confidence.
          </p>
        </div>
      )}
      
      {/* Caution Items */}
      {warnings.hasWarnings && (
        <div className="space-y-3">
          <div className="p-4 bg-amber-50 rounded-xl border border-amber-200">
            <div className="flex items-start gap-3">
              <Info size={20} className="text-amber-600 mt-0.5" />
              <div>
                <h4 className="font-medium text-amber-800 mb-1">Review recommended</h4>
                <p className="text-sm text-amber-700">
                  Some items may not need disputing. Review the flagged items below. 
                  You can still proceed with all items if you believe they contain inaccurate information.
                </p>
              </div>
            </div>
          </div>
          
          {/* Caution Accounts */}
          {warnings.accounts.caution.length > 0 && (
            <div className="border border-amber-200 rounded-xl overflow-hidden">
              <div className="p-3 bg-amber-50 border-b border-amber-200 flex items-center gap-2">
                <ShieldAlert size={16} className="text-amber-600" />
                <span className="font-medium text-amber-800">
                  Positive/Current Accounts ({warnings.accounts.caution.length})
                </span>
              </div>
              <div className="p-3 space-y-2">
                {warnings.accounts.caution.map((account, idx) => (
                  <div key={idx} className="flex items-center gap-2 text-sm">
                    <AlertTriangle size={14} className="text-amber-500" />
                    <span className="text-slate-700">
                      {account.account_name || account.creditor_name}
                    </span>
                    <span className="text-xs text-slate-500">- {account.reason}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Caution Inquiries */}
          {warnings.inquiries.caution.length > 0 && (
            <div className="border border-amber-200 rounded-xl overflow-hidden">
              <div className="p-3 bg-amber-50 border-b border-amber-200 flex items-center gap-2">
                <ShieldAlert size={16} className="text-amber-600" />
                <span className="font-medium text-amber-800">
                  Inquiries Linked to Open Accounts ({warnings.inquiries.caution.length})
                </span>
              </div>
              <div className="p-3 space-y-2">
                {warnings.inquiries.caution.map((inquiry, idx) => (
                  <div key={idx} className="flex items-center gap-2 text-sm">
                    <AlertTriangle size={14} className="text-amber-500" />
                    <span className="text-slate-700">
                      {inquiry.company_name || inquiry.creditor}
                    </span>
                    <span className="text-xs text-slate-500">- {inquiry.reason}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * Bureau Targets Step Component
 */
const BureauTargetsStep = ({ bureauTargets, setBureauTargets }) => {
  const bureaus = ['TransUnion', 'Experian', 'Equifax'];
  
  return (
    <div className="space-y-4">
      <div className="p-4 bg-slate-50 rounded-xl border border-slate-200">
        <h4 className="font-medium text-stone-800 mb-2">Select target bureaus</h4>
        <p className="text-sm text-slate-600">
          Choose which credit bureaus to send dispute letters to. Most items appear on 
          all three bureaus, but you can customize this if needed.
        </p>
      </div>
      
      <div className="grid grid-cols-3 gap-4">
        {bureaus.map(bureau => {
          const isSelected = bureauTargets.includes(bureau);
          return (
            <button
              key={bureau}
              onClick={() => {
                if (isSelected) {
                  setBureauTargets(bureauTargets.filter(b => b !== bureau));
                } else {
                  setBureauTargets([...bureauTargets, bureau]);
                }
              }}
              className={cn(
                'p-6 rounded-xl border-2 transition-all',
                isSelected 
                  ? 'border-emerald-500 bg-emerald-50' 
                  : 'border-slate-200 hover:border-slate-300'
              )}
            >
              <div className={cn(
                'w-12 h-12 rounded-full mx-auto mb-3 flex items-center justify-center',
                isSelected ? 'bg-emerald-500' : 'bg-slate-200'
              )}>
                {isSelected ? (
                  <CheckCircle size={24} className="text-white" />
                ) : (
                  <Target size={24} className="text-slate-400" />
                )}
              </div>
              <p className={cn(
                'font-medium',
                isSelected ? 'text-emerald-700' : 'text-slate-600'
              )}>
                {bureau}
              </p>
            </button>
          );
        })}
      </div>
      
      {bureauTargets.length === 0 && (
        <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
          <p className="text-sm text-amber-700 flex items-center gap-2">
            <AlertTriangle size={16} />
            Please select at least one bureau to continue.
          </p>
        </div>
      )}
    </div>
  );
};

/**
 * Confirm Step Component
 */
const ConfirmStep = ({ selectedItems, categoryCounts, bureauTargets }) => {
  const totalItems = Object.values(categoryCounts).reduce((a, b) => a + b, 0);
  
  return (
    <div className="space-y-4">
      <div className="p-6 bg-emerald-50 rounded-xl border border-emerald-200 text-center">
        <CheckCircle size={48} className="mx-auto text-emerald-500 mb-4" />
        <h4 className="text-xl font-semibold text-emerald-800 mb-2">Ready to Create Dispute Package</h4>
        <p className="text-sm text-emerald-600">
          Review the summary below and click "Create Dispute Package" to proceed.
        </p>
      </div>
      
      {/* Summary */}
      <div className="grid grid-cols-2 gap-4">
        <div className="p-4 bg-slate-50 rounded-xl border border-slate-200">
          <p className="text-sm text-slate-500 mb-1">Total Items</p>
          <p className="text-2xl font-bold text-stone-800">{totalItems}</p>
        </div>
        <div className="p-4 bg-slate-50 rounded-xl border border-slate-200">
          <p className="text-sm text-slate-500 mb-1">Target Bureaus</p>
          <p className="text-2xl font-bold text-stone-800">{bureauTargets.length}</p>
        </div>
      </div>
      
      {/* Category Breakdown */}
      <div className="border border-slate-200 rounded-xl overflow-hidden">
        <div className="p-3 bg-slate-50 border-b border-slate-200">
          <span className="font-medium text-stone-800">Items by Category</span>
        </div>
        <div className="divide-y divide-slate-100">
          {Object.entries(categoryCounts)
            .filter(([_, count]) => count > 0)
            .map(([category, count]) => {
              const Icon = CATEGORY_ICONS[category];
              return (
                <div key={category} className="flex items-center justify-between p-3">
                  <div className="flex items-center gap-2">
                    <Icon size={16} className="text-slate-500" />
                    <span className="text-sm text-slate-700">{CATEGORY_LABELS[category]}</span>
                  </div>
                  <Badge variant="secondary">{count}</Badge>
                </div>
              );
            })}
        </div>
      </div>
      
      {/* Bureau Targets */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-sm text-slate-500">Sending to:</span>
        {bureauTargets.map(bureau => (
          <Badge key={bureau} className="bg-emerald-100 text-emerald-700">
            {bureau}
          </Badge>
        ))}
      </div>
    </div>
  );
};

/**
 * Main BulkDisputeWizard Component
 */
export const BulkDisputeWizard = ({ tradelines = [] }) => {
  const navigate = useNavigate();
  const {
    selectedItems,
    categoryCounts,
    removeItem,
    clearAll,
    showBulkWizard,
    closeBulkWizard,
    totalCount
  } = useDisputeTray();
  
  const [currentStep, setCurrentStep] = useState('review');
  const [bureauTargets, setBureauTargets] = useState(['TransUnion', 'Experian', 'Equifax']);
  
  const currentStepIndex = STEPS.findIndex(s => s.id === currentStep);
  
  const canProceed = useMemo(() => {
    switch (currentStep) {
      case 'review':
        return totalCount > 0;
      case 'warnings':
        return true; // Warnings are advisory
      case 'bureaus':
        return bureauTargets.length > 0;
      case 'confirm':
        return true;
      default:
        return false;
    }
  }, [currentStep, totalCount, bureauTargets]);
  
  const handleNext = () => {
    if (currentStepIndex < STEPS.length - 1) {
      setCurrentStep(STEPS[currentStepIndex + 1].id);
    }
  };
  
  const handleBack = () => {
    if (currentStepIndex > 0) {
      setCurrentStep(STEPS[currentStepIndex - 1].id);
    }
  };
  
  const handleCreateDispute = () => {
    // Navigate to dispute center with all selected items
    navigate('/disputes', {
      state: {
        bulkDispute: true,
        items: selectedItems,
        bureauTargets,
        categoryCounts
      }
    });
    closeBulkWizard();
    clearAll();
  };
  
  const handleClose = () => {
    setCurrentStep('review');
    closeBulkWizard();
  };
  
  return (
    <Dialog open={showBulkWizard} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield size={20} className="text-emerald-600" />
            Create Dispute Package
          </DialogTitle>
        </DialogHeader>
        
        {/* Progress Bar */}
        <ProgressBar currentStep={currentStep} steps={STEPS} />
        
        {/* Step Content */}
        <div className="flex-1 overflow-y-auto">
          {currentStep === 'review' && (
            <ReviewStep 
              selectedItems={selectedItems}
              onRemoveItem={removeItem}
              categoryCounts={categoryCounts}
            />
          )}
          
          {currentStep === 'warnings' && (
            <WarningsStep 
              selectedItems={selectedItems}
              tradelines={tradelines}
            />
          )}
          
          {currentStep === 'bureaus' && (
            <BureauTargetsStep 
              bureauTargets={bureauTargets}
              setBureauTargets={setBureauTargets}
            />
          )}
          
          {currentStep === 'confirm' && (
            <ConfirmStep 
              selectedItems={selectedItems}
              categoryCounts={categoryCounts}
              bureauTargets={bureauTargets}
            />
          )}
        </div>
        
        {/* Navigation */}
        <div className="flex items-center justify-between pt-4 border-t border-slate-200 mt-4">
          <Button
            variant="outline"
            onClick={currentStepIndex === 0 ? handleClose : handleBack}
          >
            <ChevronLeft size={16} className="mr-1" />
            {currentStepIndex === 0 ? 'Cancel' : 'Back'}
          </Button>
          
          {currentStep === 'confirm' ? (
            <Button
              onClick={handleCreateDispute}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              disabled={!canProceed}
            >
              <CheckCircle size={16} className="mr-2" />
              Create Dispute Package
            </Button>
          ) : (
            <Button
              onClick={handleNext}
              disabled={!canProceed}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              Continue
              <ChevronRight size={16} className="ml-1" />
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default BulkDisputeWizard;
